import React, { useState, useEffect, useRef } from 'react';
import {
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Award,
  Trophy,
  Clock,
  Brain,
  BarChart3,
  Sparkles,
  Target,
  Shield,
  Users,
  Calendar,
  FileText,
  AlertCircle
} from 'lucide-react';
import SectionControls from '../shared/SectionControls';

const ResultsSection = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const metrics = data.metrics;
  const [isInView, setIsInView] = useState(false);
  const [counters, setCounters] = useState({
    tasks: 0,
    hours: 0,
    stress: 100,
    completionRate: 0
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          // Animate counters
          const duration = 2000;
          const steps = 60;
          const interval = duration / steps;

          let currentStep = 0;
          const timer = setInterval(() => {
            currentStep++;
            const progress = currentStep / steps;

            setCounters({
              tasks: Math.floor(73 - (73 - 14) * progress),
              hours: Math.floor(30 * progress),
              stress: Math.floor(87 - (87 - 34) * progress),
              completionRate: Math.floor(100 * progress)
            });

            if (currentStep >= steps) {
              clearInterval(timer);
            }
          }, interval);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-6 lg:mb-12">

        <h2 className="text-2xl lg:text-4xl font-bold text-slate-900 mb-2 lg:mb-4">
          You're Ready for This
        </h2>
        <p className="text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto">
        Go from 'I'm drowning in deadlines' to 'I've got this handled' - and see how that confidence shows up in your applications.
        </p>
      </div>

      {/* Transformation Metrics */}
      <div className="grid lg:grid-cols-4 gap-6 mb-12">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 text-center"
          style={{
            animation: isInView ? 'fadeInScale 0.6s ease-out forwards' : 'none',
            animationDelay: '0ms'
          }}
        >
          <div className="w-12 h-12 bg-azure-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <BarChart3 className="w-6 h-6 text-azure-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">
            {counters.tasks}
            <span className="text-lg text-slate-500 ml-1">→ 8</span>
          </div>
          <div className="text-sm text-slate-600">Tasks to Sessions</div>
          <div className="text-xs text-emerald-600 font-medium mt-2">73% reduction</div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 text-center"
          style={{
            animation: isInView ? 'fadeInScale 0.6s ease-out forwards' : 'none',
            animationDelay: '100ms'
          }}
        >
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Clock className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="text-3xl font-bold text-emerald-600 mb-1">
            {counters.hours}h
          </div>
          <div className="text-sm text-slate-600">Hours Saved</div>
          <div className="text-xs text-emerald-600 font-medium mt-2">27% efficiency gain</div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 text-center"
          style={{
            animation: isInView ? 'fadeInScale 0.6s ease-out forwards' : 'none',
            animationDelay: '200ms'
          }}
        >
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Brain className="w-6 h-6 text-yellow-600" />
          </div>
          <div className="text-3xl font-bold text-slate-900 mb-1">
            {counters.stress}%
            <span className="text-lg text-emerald-600 ml-1">↓</span>
          </div>
          <div className="text-sm text-slate-600">Stress Level</div>
          <div className="text-xs text-emerald-600 font-medium mt-2">61% reduction</div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 text-center"
          style={{
            animation: isInView ? 'fadeInScale 0.6s ease-out forwards' : 'none',
            animationDelay: '300ms'
          }}
        >
          <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Target className="w-6 h-6 text-violet-600" />
          </div>
          <div className="text-3xl font-bold text-violet-600 mb-1">
            {counters.completionRate}%
          </div>
          <div className="text-sm text-slate-600">Completion Rate</div>
          <div className="text-xs text-emerald-600 font-medium mt-2">All deadlines met</div>
        </div>
      </div>

      {/* Before/After Comparison */}
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {/* Before */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="font-semibold text-slate-900 text-lg">October 1st: Overwhelmed</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-400" />
                Scattered Tasks
              </span>
              <span className="font-bold text-red-600">{metrics.before.totalTasks}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                Overdue Items
              </span>
              <span className="font-bold text-red-600">{metrics.before.overdueeTasks}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <Brain className="w-4 h-4 text-slate-400" />
                Stress Level
              </span>
              <span className="font-bold text-red-600">{metrics.before.stressLevel}/10</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                Estimated Hours
              </span>
              <span className="font-bold text-red-600">{metrics.before.hoursEstimated}h</span>
            </div>

            <div className="mt-4 p-3 bg-red-100/50 rounded-lg">
              <p className="text-sm text-red-700 italic">
                "I don't know where to start. Every task feels urgent and I'm constantly worried I'm forgetting something important."
              </p>
            </div>
          </div>
        </div>

        {/* After */}
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-50 rounded-xl p-6 border border-emerald-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900 text-lg">November 25th: In Control</h3>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-slate-400" />
                Smart Sessions
              </span>
              <span className="font-bold text-emerald-600">{metrics.after.totalBatches}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <Target className="w-4 h-4 text-slate-400" />
                Applications Complete
              </span>
              <span className="font-bold text-emerald-600">{metrics.after.completionRate}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-400" />
                Stress Level
              </span>
              <span className="font-bold text-emerald-600">{metrics.after.stressLevel}/10</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/80 rounded-lg">
              <span className="text-sm text-slate-700 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-400" />
                Time Saved
              </span>
              <span className="font-bold text-emerald-600">{metrics.after.hoursSaved}h</span>
            </div>

            <div className="mt-4 p-3 bg-emerald-100/50 rounded-lg">
              <p className="text-sm text-emerald-700 italic">
                "Everything is organized and I know exactly what to work on each day. I actually have time for my other activities now."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section Controls - for last section */}
      <SectionControls
        onReplay={() => window.location.reload()}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={true}
        className="mt-8"
      />

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ResultsSection;